import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { toast } from "react-toastify";
import api from "../../api";
import { PURPLE } from "./ResourceAllocationShared";

/**
 * Correct a group-content row (tutor_resources) that has already been uploaded.
 *
 * This is not the master-library editor: that one writes Topic media. Here the admin edits the very
 * row this group's students read, which is why the file swap is safe by default — replacing it does
 * not hide it. "Send back for review" is the explicit way to pull it from students again.
 *
 * Plain useState, like the tabs it sits behind (GroupContentTab, StaffAssignmentTab); the group
 * upload form in CourseContent.jsx uses react-hook-form, but nothing on this page does.
 */
export default function EditGroupResourceDialog({
  open,
  resource,
  planId,
  acdemicCourseId,
  weeks = [],
  onClose,
  onSaved,
}) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [weekId, setWeekId] = useState("");
  const [subjectId, setSubjectId] = useState("");
  const [topicId, setTopicId] = useState("");
  const [subtopicId, setSubtopicId] = useState("");
  const [file, setFile] = useState(null);
  const [reopenReview, setReopenReview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [subjects, setSubjects] = useState([]);
  const [topics, setTopics] = useState([]);
  const [subtopics, setSubtopics] = useState([]);

  const isPublished = resource?.approval_status === "approved";

  // Seed the form from the row each time the dialog opens on a different resource.
  useEffect(() => {
    if (!open || !resource) return;
    setTitle(resource.title || "");
    setDescription(resource.description || "");
    setWeekId(resource.week?.id || "");
    setSubjectId(resource.subject?.id || "");
    setTopicId(resource.topic?.id || "");
    setSubtopicId(resource.subtopic?.id || "");
    setFile(null);
    setReopenReview(false);
    setError("");
  }, [open, resource]);

  // Same three endpoints the Content Allocation tab uses, so both pickers offer the same tree.
  useEffect(() => {
    if (!open || !planId) return undefined;
    let cancelled = false;
    api
      .get(`admin/course-plans/${planId}/content/subjects`)
      .then((r) => {
        if (!cancelled) setSubjects(r.data?.data || []);
      })
      .catch(() => {
        if (!cancelled) setSubjects([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, planId]);

  useEffect(() => {
    if (!open || !acdemicCourseId || !subjectId) {
      setTopics([]);
      return undefined;
    }
    let cancelled = false;
    api
      .get(`admin/fetch/topics/by-academic-subject/${acdemicCourseId}/${subjectId}`)
      .then((r) => {
        if (!cancelled) setTopics(r.data?.data || []);
      })
      .catch(() => {
        if (!cancelled) setTopics([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, acdemicCourseId, subjectId]);

  useEffect(() => {
    if (!open || !topicId) {
      setSubtopics([]);
      return undefined;
    }
    let cancelled = false;
    api
      .get(`admin/fetch/topics/subtopics/${topicId}`)
      .then((r) => {
        if (!cancelled) setSubtopics(r.data?.data || []);
      })
      .catch(() => {
        if (!cancelled) setSubtopics([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, topicId]);

  // The row's own subject may not be in the plan's list (it was filed before the taxonomy moved on);
  // keep it selectable so an edit cannot silently drop it.
  const subjectOptions = useMemo(() => {
    const options = subjects.map((s) => ({ id: s.subject_id, name: s.subject_name }));
    if (resource?.subject?.id && !options.some((o) => String(o.id) === String(resource.subject.id))) {
      options.unshift({ id: resource.subject.id, name: resource.subject.name });
    }
    return options;
  }, [subjects, resource]);

  const canSave = Boolean(title.trim()) && Boolean(subjectId) && !saving;

  const submit = async () => {
    if (!canSave) return;
    setSaving(true);
    setError("");
    try {
      const form = new FormData();
      // PHP never populates $_FILES on a real PUT, so the update is posted with a spoofed method.
      form.append("_method", "PUT");
      form.append("title", title.trim());
      form.append("description", description || "");
      form.append("plan_week_id", weekId || "");
      form.append("subject_id", subjectId);
      form.append("topic_id", topicId || "");
      form.append("subtopic_id", subtopicId || "");
      if (reopenReview) form.append("reopen_review", "1");
      if (file) form.append("file", file);

      const res = await api.post(`admin/tutor-resources/${resource.id}`, form);
      toast.success(res.data?.message || "Saved.");
      onSaved?.();
      onClose?.();
    } catch (e) {
      const message =
        e?.response?.data?.data?.error ||
        e?.response?.data?.message ||
        "Could not save these changes.";
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose?.()} maxWidth="sm" fullWidth>
      <DialogTitle>Edit group content</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <TextField
            fullWidth
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            error={!title.trim()}
          />
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />

          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6}>
              <FormControl size="small" fullWidth>
                <InputLabel>Week</InputLabel>
                <Select value={weekId} label="Week" onChange={(e) => setWeekId(e.target.value)}>
                  <MenuItem value="">No specific week</MenuItem>
                  {weeks.map((w) => (
                    <MenuItem key={w.id} value={w.id}>
                      Week {w.week_number}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl size="small" fullWidth required error={!subjectId}>
                <InputLabel>Subject</InputLabel>
                <Select
                  value={subjectId}
                  label="Subject"
                  onChange={(e) => {
                    setSubjectId(e.target.value);
                    setTopicId("");
                    setSubtopicId("");
                  }}
                >
                  {subjectOptions.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl size="small" fullWidth disabled={!subjectId}>
                <InputLabel>Topic</InputLabel>
                <Select
                  value={topicId}
                  label="Topic"
                  onChange={(e) => {
                    setTopicId(e.target.value);
                    setSubtopicId("");
                  }}
                >
                  <MenuItem value="">No topic</MenuItem>
                  {topics.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl size="small" fullWidth disabled={!topicId}>
                <InputLabel>Sub topic</InputLabel>
                <Select
                  value={subtopicId}
                  label="Sub topic"
                  onChange={(e) => setSubtopicId(e.target.value)}
                >
                  <MenuItem value="">No sub topic</MenuItem>
                  {subtopics.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Box>
            <Button
              component="label"
              variant="outlined"
              startIcon={<UploadFileIcon />}
              sx={{ textTransform: "none", borderColor: "#e5e7eb", color: "text.primary" }}
            >
              {file ? "Change the replacement file" : "Replace file (optional)"}
              <input
                hidden
                type="file"
                accept=".pdf,.ppt,.pptx,.doc,.docx,.mp4,.mov,.avi,.wmv,.jpg,.jpeg,.png"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </Button>
            <Typography variant="caption" display="block" sx={{ mt: 0.75, color: "#374151" }}>
              {file
                ? `${file.name} replaces the current file.`
                : isPublished
                  ? "Leave empty to keep the current file. A replacement is visible to students straight away."
                  : "Leave empty to keep the current file."}
            </Typography>
          </Box>

          <Box sx={{ bgcolor: "#f8fafc", borderRadius: 1.5, p: 1.25 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={reopenReview}
                  onChange={(e) => setReopenReview(e.target.checked)}
                  sx={{ color: PURPLE, "&.Mui-checked": { color: PURPLE } }}
                />
              }
              label="Send back for review"
            />
            <Typography variant="caption" display="block" sx={{ color: "#374151", pl: 4 }}>
              {reopenReview
                ? "Students lose access until it is approved again on this tab."
                : "Off: the content stays exactly as visible to students as it is now."}
            </Typography>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose?.()} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={submit} disabled={!canSave}>
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
